'use client';

import { useCallback, useMemo, useState } from 'react';
import { Button } from '@opaz/ui-components';
import UserSatisfactionForm from '@/components/user-satisfaction-form';
import { useTranslation } from 'react-i18next';
import { useStrapiPost } from '@/hooks/useStrapiPost';
import { useAnalytics } from '@/hooks/useAnalytics';

export default function UserSatisfaction() {
  const { t } = useTranslation();
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const analytics = useAnalytics();

  const pageUrl = useMemo(() => (typeof window !== 'undefined' ? window.location.href : ''), []);

  const { mutateAsync, isPending, isSuccess, isError, error } = useStrapiPost('user-satisfactions');

  const handleSatisfactionClick = useCallback(
    async (satisfied: boolean) => {
      if (analytics) {
        analytics.trackUserSatisfaction(satisfied);
      }

      await mutateAsync({
        pageContent: pageUrl,
        satisfied,
        skipRecaptcha: true,
      });
    },
    [mutateAsync, pageUrl, analytics],
  );

  return (
    <div className="bgGradient bottom-0 left-0 right-0 z-40">
      <div className="max-w-container-960 mx-auto flex flex-col gap-4 py-6">
        {isSuccess ? (
          <p className="font-bold text-center md:text-start">{t('userSatisfaction.thankYou')}</p>
        ) : !showFeedbackForm ? (
          <div className="flex flex-col md:flex-row md:justify-between items-center gap-6 md:gap-0">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <p className="text-center md:text-start">{t('userSatisfaction.question')}</p>
              <div className="flex justify-center gap-4">
                <Button
                  className="w-32"
                  variant="secondary"
                  onClick={() => handleSatisfactionClick(true)}
                  disabled={isPending}
                >
                  {t('userSatisfaction.yes')}
                </Button>
                <Button
                  className="w-32"
                  variant="secondary"
                  onClick={() => handleSatisfactionClick(false)}
                  disabled={isPending}
                >
                  {t('userSatisfaction.no')}
                </Button>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => setShowFeedbackForm(true)}
              className="underline text-link font-light hover:bg-transparent"
            >
              {t('userSatisfaction.reportProblem')}
            </Button>
          </div>
        ) : (
          <UserSatisfactionForm onCancel={() => setShowFeedbackForm(false)} />
        )}
        {isError && error && <p className="text-sm text-red-500 mt-2">{t('common.errorMessagePostMethod')}</p>}
      </div>
    </div>
  );
}
